import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">电影未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">您要查找的电影可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 4);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Movie",
    "name": movie.title,
    "alternateName": movie.titleEn,
    "description": movie.description,
    "dateCreated": `${movie.year}`,
    "director": {
      "@type": "Person",
      "name": movie.director,
    },
    "actor": movie.actors.map((a) => ({
      "@type": "Person",
      "name": a,
    })),
    "countryOfOrigin": movie.region,
    "duration": movie.duration,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: movie.category, href: `/category/${movie.categorySlug}` },
              { label: movie.title },
            ]}
          />

          <div className="px-4 md:px-6 pb-10">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0" style={{ width: "300px" }}>
                <div className="rounded-lg overflow-hidden" style={{ width: "300px", height: "420px" }}>
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    title={`${movie.title} - 光棍影院免费高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-sm font-medium">
                    {movie.category}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <strong>{movie.rating}</strong>
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-4 rounded-lg bg-background-100">
                  <div>
                    <span className="text-xs text-foreground-500">导演：</span>
                    <span className="text-sm text-foreground-900 font-medium">{movie.director}</span>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">主演：</span>
                    <span className="text-sm text-foreground-900 font-medium">{movie.actors.join("、")}</span>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">地区：</span>
                    <span className="text-sm text-foreground-900">{movie.region}</span>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">语言：</span>
                    <span className="text-sm text-foreground-900">中文/原声</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-base font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                    剧情简介
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{movie.longDescription}</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${movie.categorySlug}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-white rounded-md text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-play-fill"></i>
                    立即播放
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-md text-sm font-semibold whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>

          {relatedMovies.length > 0 && (
            <div className="px-4 md:px-6 pb-10 md:pb-14">
              <div className="border-t border-background-200 pt-8">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}